import { useRef, useState, useEffect } from 'react';
import cn from 'classnames';

import useResize from 'js/hooks/use-resize';
import { defineTheme, hasTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Heading from 'components/heading/Heading';
import Link from 'components/link/Link';
import Metadata, { MetadataTheme } from 'components/metadata/Metadata';
import Message from 'components/message/Message';

import DateCardStatus from './DateCardStatus';
import DateCardTags from './DateCardTags';
import DateCardDates from './DateCardDates';
import DateCardMedia from './DateCardMedia';

import { DateCardStatus_Theme, IDateCard } from 'source/NFR.types';

const themes = defineTheme({
  rightAlignedTags: 'date-card--theme-right-aligned-tags',
  noPadding: 'date-card--theme-no-padding',
  noBorder: 'date-card--theme-no-border',
  sidebar: 'date-card--theme-sidebar',
  vertical: 'date-card--theme-vertical'
});
export type DateCardTheme = ThemeKeys<typeof themes>;

const useGetHeightOfName = (ref: React.RefObject<HTMLHeadingElement>) => {
  const [nameHeight, setNameHeight] = useState<number>(0);

  useEffect(() => {
    if (!ref.current) return;
    const nameHeight = ref.current.offsetHeight;
    setNameHeight(nameHeight);
  }, [ref]);

  useResize(() => setNameHeight((ref.current && ref.current.offsetHeight) ?? 0));

  return nameHeight;
};

export interface IDateCardProps extends IDateCard {
  isImageLink?: boolean;
  headingLevel?: number;
  metadataTheme?: MetadataTheme;
  showMedia?: boolean;
  theme?: DateCardTheme | DateCardTheme[];
}

const DateCard: React.FC<IDateCardProps> = ({
  dateContainer,
  headingLevel,
  media,
  metadata,
  metadataTheme,
  messageList,
  showMedia,
  subtitle,
  status,
  tags,
  theme,
  title,
  text,
  url,
  usedInSidebar,
  isImageLink,
  name
}) => {
  const { dates = [] } = dateContainer;
  const hasDate = dates.length || dateContainer.date;

  const nameRef = useRef<HTMLHeadingElement>(null);
  const heightOfName = useGetHeightOfName(nameRef);
  const statusTheme = dateContainer.isPlanned
    ? DateCardStatus_Theme.IsPlanned
    : dateContainer.isPastDate
      ? DateCardStatus_Theme.IsCompleted
      : DateCardStatus_Theme.None;

  return (
    <div
      className={cn('date-card', themes(theme), {
        'date-card--without-date': !hasDate
      })}
    >
      {name && (
        <Heading ref={nameRef} className="date-card--name">
          {name}
        </Heading>
      )}
      <div className="date-card__grid date-card__grid-primary">
        <Link className="date-card--title" url={url}>
          {title && (
            <Heading className="date-card--heading" level={headingLevel}>
              {title}
            </Heading>
          )}
          {subtitle && (
            <Heading className="date-card--sub-heading" level={(headingLevel ?? 0) + 1}>
              {subtitle}
            </Heading>
          )}
        </Link>

        {hasDate && (
          <DateCardDates
            {...dateContainer}
            theme={hasTheme<DateCardTheme>(theme, 'vertical') ? 'big' : undefined}
            url={isImageLink ? url : undefined}
            heightOfName={heightOfName}
          />
        )}
        <div className="date-card--content">
          {messageList?.map(message => (
            <div key={message?.text?.text} className="date-card--message">
              <Message {...message} />
            </div>
          ))}
          {metadata && (
            <div className="date-card--metadata">
              <Metadata theme={usedInSidebar ? 'vertical' : metadataTheme} {...metadata} />
            </div>
          )}
          {showMedia && media && <DateCardMedia {...media} />}
          {text && <p className="date-card--text">{text}</p>}
        </div>
      </div>
      {(status || tags) && (
        <div className="date-card__grid date-card__grid-secondary">
          {status && <DateCardStatus {...status} centered={!usedInSidebar} theme={statusTheme} />}

          {tags && <DateCardTags {...tags} />}
        </div>
      )}
    </div>
  );
};

DateCard.displayName = 'DateCard';

export default DateCard;
